import { FiChevronRight } from "react-icons/fi";
import { FaServer, FaDatabase, FaEye, FaNetworkWired } from "react-icons/fa";
import { HiOutlineClock, HiOutlineDocumentText } from "react-icons/hi";
import observabilityDarkImg from "../../assets/observability1.png";
import observabilityLightImg from "../../assets/observability3.png";
import DiaDarkImg from "../../assets/observability_dark_transparent (1).png"
import DiaLightImg from "../../assets/observability_light_transparent.png"
import { MdKeyboardArrowRight, MdArrowForward } from 'react-icons/md';

export function rendercoreConcept() {
  return (
    <div className="transition-opacity duration-300">
      {/* Breadcrumb */}
      <nav className="flex flex-wrap items-center gap-2 text-sm text-[#9a734c]">
        <a className="hover:text-primary hover:underline" href="#">Netraa APM</a>
        <FiChevronRight className="text-[14px]" />
        <span>Core Concepts</span>
      </nav>
      {/* Core Concepts Header */}
      <div className="border-b border-gray-200 dark:border-gray-800 mt-8 pb-8 mb-1">
        <h1 className="text-3xl lg:text-5xl font-extrabold text-text-main dark:text-white tracking-tight mb-6">
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-red-600">Core Concepts</span><span className="text-orange-500">:</span><br></br>
          Foundations of Full-Stack Observability
        </h1>
        <p className="text-xl text-text-muted dark:text-gray-400 leading-relaxed">
          At the heart of Netraa APM lies a set of foundational models that transform raw data into actionable insights.
          Understanding these core concepts is essential for leveraging the platform's full potential in identifying Root Cause Analysis (RCA) and ensuring system reliability.
        </p>
      </div>

      {/* Services, Instances, and Endpoints */}
      <section className="scroll-mt-32 mb-1 mt-6" id="service-model">
        <h2 className="text-2xl font-bold text-text-main dark:text-white mb-6 flex items-center gap-3">
          <span className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400">
            <FaServer size={18} />
          </span>
          Services, Instances, and Endpoints
        </h2>

        <p className="text-base text-text-muted dark:text-gray-400 mb-8 leading-relaxed">
          A <strong>Service</strong> is a logical group of code providing a specific functionality (e.g., 'Payment Service').
          An <strong>Instance</strong> is a single running process of that service (e.g., a pod in K8s or a JVM).
          <strong>Endpoints</strong> are the specific entry points (API paths or methods) exposed by the service.
        </p>

        {/* MAIN CONTAINER */}
        <div className="flex-1 flex justify-center">
          <div className="relative w-100 h-100">

            {/* Light Mode Image */}
            <img
              src={DiaLightImg}
              alt="Observability diagram"
              className="w-90 h-90 object-contain block dark:hidden"
            />

            {/* Dark Mode Image */}
            <img
              src={DiaDarkImg}
              alt="Observability diagram"
              className="w-100 h-100 object-contain hidden dark:block"
            />

          </div>
        </div>
      </section>

      {/* Traces vs Logs vs Metrics */}
      <section className="scroll-mt-32 mt-6 mb-1" id="telemetry-correlation">
        <h2 className="text-2xl font-bold text-text-main dark:text-white mb-2 flex items-center gap-3">
          <span className="p-2 rounded-lg bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400">
            <HiOutlineClock size={20} />
          </span>
          Traces vs Logs vs Metrics
        </h2>
        <div className="flex flex-col lg:flex-row gap-12 items-center">
          <div className="flex-1 space-y-6">
            {/* Metrics */}
            <div className="flex gap-4 items-start">
              <div className="mt-1 size-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
                <FaDatabase className="text-blue-600 dark:text-blue-400 text-sm" />
              </div>
              <div>
                <h4 className="text-base font-bold text-text-main dark:text-white">Metrics</h4>
                <p className="text-sm text-text-muted mt-1 leading-relaxed">
                  Provide aggregated health data (trends, CPU, memory usage) over time. Perfect for spotting anomalies in system behavior.
                </p>
              </div>
            </div>
            {/* Traces */}
            <div className="flex gap-4 items-start">
              <div className="mt-1 size-8 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center shrink-0">
                <HiOutlineClock className="text-orange-600 dark:text-orange-400 text-sm" />
              </div>
              <div>
                <h4 className="text-base font-bold text-text-main dark:text-white">Traces</h4>
                <p className="text-sm text-text-muted mt-1 leading-relaxed">
                  Track the flow of a single request across distributed services. Essential for understanding latency and service dependencies.
                </p>
              </div>
            </div>
            {/* Logs */}
            <div className="flex gap-4 items-start">
              <div className="mt-1 size-8 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center shrink-0">
                <HiOutlineDocumentText className="text-teal-600 dark:text-teal-400 text-sm" />
              </div>
              <div>
                <h4 className="text-base font-bold text-text-main dark:text-white">Logs</h4>
                <p className="text-sm text-text-muted mt-1 leading-relaxed">
                  Offer detailed, discrete event context for debugging specific errors. Netraa correlates these three signals to provide complete observability.
                </p>
              </div>
            </div>
          </div>
          <div className="flex-1 flex justify-center">
            <div className="relative w-100 h-100">

              {/* Light Mode Image */}
              <img
                src={observabilityLightImg}
                alt="Observability diagram"
                className="w-90 h-90 object-contain block dark:hidden"
              />

              {/* Dark Mode Image */}
              <img
                src={observabilityDarkImg}
                alt="Observability diagram"
                className="w-100 h-100 object-contain hidden dark:block"
              />

            </div>
          </div>
        </div>
        {/* <div className="flex-1 flex justify-center">
  <div className="relative w-100 h-100">
    <img
      src={observabilityImg}
      alt="Metrics, traces, and logs correlation diagram"
      className="w-100 h-100 object-contain"
    />
  </div>
</div> */}
      </section>

      {/* Infrastructure Groups */}
      <section className="scroll-mt-32 mt-6 mb-1" id="infra-groups">
        <h2 className="text-2xl font-bold text-text-main dark:text-white mb-6 flex items-center gap-3">
          <span className="p-2 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400">
            <FaNetworkWired size={18} />
          </span>
          Infrastructure Groups
        </h2>
        <div className="bg-surface-light dark:bg-[#1A1C20] p-8 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-card">
          <p className="text-base text-text-muted dark:text-gray-400 mb-6 leading-relaxed">
            Infrastructure is organized logically into Groups based on metadata. Groups can be defined by <strong>Environment</strong> (Prod, Staging), <strong>Geography</strong> (US-East, EU-West), or custom tags. This allows you to visualize health impact across logical boundaries rather than just individual hosts.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 flex items-center gap-4 bg-gray-50 dark:bg-white/5">
              <div className="p-3 bg-white dark:bg-black rounded-lg shadow-sm">
                <FaNetworkWired className="text-netraa-orange-start" />
              </div>
              <div>
                <h4 className="font-bold text-sm text-text-main dark:text-white">Geography</h4>
                <p className="text-xs text-text-muted mt-1">us-east-1, eu-central-1</p>
              </div>
            </div>
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 flex items-center gap-4 bg-gray-50 dark:bg-white/5">
              <div className="p-3 bg-white dark:bg-black rounded-lg shadow-sm">
                <FaServer className="text-blue-500" />
              </div>
              <div>
                <h4 className="font-bold text-sm text-text-main dark:text-white">Environment</h4>
                <p className="text-xs text-text-muted mt-1">production, staging, dev</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Promo Banner */}
      <div
        id="promo-banner"
        className="relative overflow-hidden rounded-2xl mb-4 scroll-mt-24
             bg-white dark:bg-[#0A0E1A]
             border border-gray-200 dark:border-white/10
             shadow-sm dark:shadow-[0_0_40px_rgba(139,92,246,0.08)]"
      >
        {/* Left gradient accent bar */}
        <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-purple-500 via-fuchsia-500 to-cyan-400" />

        {/* Subtle glow, dark mode only */}
        <div className="pointer-events-none absolute -left-10 top-1/2 -translate-y-1/2 w-40 h-40 rounded-full bg-purple-500/20 blur-3xl hidden dark:block" />
        <div className="pointer-events-none absolute right-10 top-1/2 -translate-y-1/2 w-40 h-40 rounded-full bg-cyan-400/10 blur-3xl hidden dark:block" />

        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6 px-8 py-8 pl-10">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-cyan-600 dark:text-cyan-400 mb-3">
              Ready to Unify Your Observability?
            </div>

            <h3 className="text-2xl lg:text-3xl font-extrabold text-text-main dark:text-white mb-3 leading-tight">
              See Netraa in action.
            </h3>

            <p className="text-sm lg:text-base text-text-muted dark:text-gray-400 leading-relaxed max-w-md">
              Discover how Netraa correlates metrics, traces, and logs to accelerate root cause analysis across your infrastructure.
            </p>
          </div>

          <button
            onClick={() => (window.location.href = "https://cliqtest.com/book-a-demo")}
            className="shrink-0 inline-flex items-center gap-2 px-6 py-3.5 rounded-xl font-bold text-sm
                 bg-gradient-to-r from-orange-600 to-red-600 text-white
                 shadow-sm hover:shadow-md transition-all duration-300
                 whitespace-nowrap
                 hover:scale-[1.03] active:scale-[0.98]"
          >
            Book a Demo
            <MdArrowForward className="text-lg" />
          </button>
        </div>
      </div>
    </div>
  );
}